import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, History, Info, Network, Trash2, TrendingUp } from 'lucide-react';
import { ValeurIndiceDto } from 'api/indice';
import { Sizes } from 'enums/sizes';
import { indicesAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { GenericLoading } from 'components/ui/Loader';
import { EmptyState } from 'components/ui/EmptyState';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { BlockCard, BlockRow, BlockRows, StripedList } from 'back-office/shared/components/BlockCard';
import { RowActions } from 'back-office/shared/components/RowActions';
import { useAsyncResource } from 'back-office/shared/hooks/useAsyncResource';
import { useBranches } from 'back-office/features/branches-view/useBranches';
import { IndiceModal } from './forms/IndiceModal';
import { IndiceValueModal } from './forms/IndiceValueModal';
import { formatIndiceValue, toValuePeriods } from './indiceValues';

/**
 * Détail d'un indice : son identité, ses branches et l'historique de ses valeurs.
 *
 * L'historique est présenté en périodes calculées (« du 01/01 au 31/03 ») plutôt qu'en
 * dates isolées : le back ne stocke qu'une date de début, c'est la valeur suivante qui
 * borne la précédente. Affichées telles quelles, ces dates ne disent pas quelle valeur
 * s'applique quand — et un trou de saisie passe inaperçu.
 */
export const IndiceDetails: FC = () => {
  const { indiceId } = useParams<{ indiceId: string }>();
  const navigate = useNavigate();
  const branches = useBranches();
  const { openBlankDialog } = useDialog();
  const { createMenuItem } = useActionMenu();

  const [valueToDelete, setValueToDelete] = useState<ValeurIndiceDto | null>(null);
  const [isDeletingIndice, setIsDeletingIndice] = useState(false);
  const [isConfirmingIndiceDelete, setIsConfirmingIndiceDelete] = useState(false);
  const [isDeletingValue, setIsDeletingValue] = useState(false);

  const fetchIndice = useCallback(
    () => indicesAPI.getIndice({ indiceId: indiceId as string }),
    [indiceId]
  );
  const { data: indice, isLoading, error, reload } = useAsyncResource(
    fetchIndice,
    Boolean(indiceId)
  );

  const periods = useMemo(() => toValuePeriods(indice?.values), [indice]);

  const deleteValue = async () => {
    if (!indice || !valueToDelete?.id) return;

    setIsDeletingValue(true);
    try {
      const response = await indicesAPI.deleteIndiceValue({
        indiceId: indice.id,
        valeurId: valueToDelete.id,
      });
      if (response.isSuccess) {
        toast.success('Valeur supprimée');
        await reload();
      } else {
        toast.error(response.errorMessage || 'La suppression a échoué');
      }
    } catch (err) {
      console.error('Suppression de la valeur impossible', err);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeletingValue(false);
      setValueToDelete(null);
    }
  };

  const deleteIndice = async () => {
    if (!indice) return;

    setIsDeletingIndice(true);
    try {
      const response = await indicesAPI.deleteIndice({ indiceId: indice.id });
      if (response.isSuccess) {
        toast.success('Indice supprimé');
        navigate('/back-office/indices');
      } else {
        toast.error(response.errorMessage || 'La suppression a échoué');
      }
    } catch (err) {
      console.error("Suppression de l'indice impossible", err);
      toast.error('La suppression a échoué');
    } finally {
      setIsDeletingIndice(false);
      setIsConfirmingIndiceDelete(false);
    }
  };

  if (isLoading) {
    return (
      <section className="w-full rounded-2xl border bg-white p-8">
        <GenericLoading />
      </section>
    );
  }

  if (error || !indice) {
    return (
      <section className="w-full rounded-2xl border bg-white">
        <EmptyState
          icon={<TrendingUp className="h-8 w-8 text-slate-400" />}
          title={error ?? 'Indice introuvable'}
        />
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate('/back-office/indices')}
        className="inline-flex w-fit items-center gap-2 rounded-lg px-2 py-1 text-sm text-gray-500 transition-colors hover:bg-gray-100 hover:text-primary"
      >
        <ArrowLeft size={16} />
        Retour aux indices
      </button>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h1 className="text-2xl font-bold text-zinc-800">{indice.label || 'Indice'}</h1>
        {indice.code && (
          <span className="rounded bg-white px-2 py-0.5 font-mono text-xs text-gray-500 shadow-sm">
            {indice.code}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-2">
        <BlockCard
          title="Identité"
          icon={<Info className="h-4 w-4" />}
          actions={[
            createMenuItem(
              'edit',
              "Modifier l'indice",
              () =>
                openBlankDialog(
                  { size: Sizes['3xl'] },
                  <IndiceModal indice={indice} branches={branches} onSuccess={reload} />
                ),
              { icon: 'edit' }
            ),
            createMenuItem(
              'delete',
              "Supprimer l'indice",
              () => setIsConfirmingIndiceDelete(true),
              { icon: 'trash', variant: 'danger' }
            ),
          ]}
        >
          <BlockRows>
            <BlockRow label="Code">
              <span className="font-mono">{indice.code || '—'}</span>
            </BlockRow>
            <BlockRow label="Libellé">{indice.label || '—'}</BlockRow>
            <BlockRow label="Valeur en vigueur">
              {periods[0] ? (
                <span className="font-bold tabular-nums text-primary">
                  {formatIndiceValue(periods[0].value.value)}
                </span>
              ) : (
                '—'
              )}
            </BlockRow>
            <BlockRow label="Depuis le">{periods[0]?.startLabel ?? '—'}</BlockRow>
          </BlockRows>

          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-zinc-700">
              <Network className="h-3.5 w-3.5 text-primary" />
              Branches concernées
            </p>
            {indice.branches?.length ? (
              <div className="flex flex-wrap gap-1.5">
                {indice.branches.map((branch) => (
                  <span
                    key={branch.branchId}
                    title={branch.branchLabel ?? undefined}
                    className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                  >
                    {branch.branchCode || branch.branchLabel}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-amber-700">
                Aucune branche : cet indice n'est proposé nulle part.
              </p>
            )}
          </div>
        </BlockCard>

        <BlockCard
          title="Historique des valeurs"
          description="Chaque valeur s'applique jusqu'à la suivante"
          icon={<History className="h-4 w-4" />}
          headerExtra={
            <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold text-white">
              {periods.length}
            </span>
          }
          actions={[
            createMenuItem(
              'add-value',
              'Ajouter une valeur',
              () =>
                openBlankDialog(
                  { size: Sizes.xl },
                  <IndiceValueModal indice={indice} onSuccess={reload} />
                ),
              { icon: 'add' }
            ),
          ]}
          isEmpty={periods.length === 0}
          emptyLabel="Aucune valeur enregistrée pour cet indice"
          flushBody
        >
          <StripedList>
            {periods.map((period) => (
              <li key={period.value.id} className="flex items-center gap-4 px-5 py-2.5">
                <span className="flex-shrink-0 text-sm font-bold tabular-nums text-zinc-800">
                  {formatIndiceValue(period.value.value)}
                </span>

                <span className="min-w-0 flex-1 text-xs text-gray-500">
                  {period.endLabel
                    ? `du ${period.startLabel} au ${period.endLabel}`
                    : `depuis le ${period.startLabel}`}
                </span>

                {period.isCurrent && (
                  <span className="flex-shrink-0 rounded bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-800">
                    en vigueur
                  </span>
                )}

                <RowActions
                  actions={[
                    {
                      tone: 'danger',
                      label: 'Supprimer la valeur',
                      icon: <Trash2 className="h-4 w-4" />,
                      onClick: () => setValueToDelete(period.value),
                    },
                  ]}
                />
              </li>
            ))}
          </StripedList>
        </BlockCard>
      </div>

      <ConfirmationOverlay
        isOpen={Boolean(valueToDelete)}
        onClose={() => setValueToDelete(null)}
        onConfirm={deleteValue}
        isLoading={isDeletingValue}
        title="Supprimer cette valeur ?"
        description="La période qui la précède s'étendra jusqu'à la valeur suivante."
        confirmLabel="Supprimer"
      />

      {/*
        Le back ne pose aucun garde-fou sur la suppression d'un indice — rien ne consomme
        encore les indices, donc rien ne s'y oppose. La confirmation explicite est ici la
        seule protection contre une perte de tout l'historique.
      */}
      <ConfirmationOverlay
        isOpen={isConfirmingIndiceDelete}
        onClose={() => setIsConfirmingIndiceDelete(false)}
        onConfirm={deleteIndice}
        isLoading={isDeletingIndice}
        variant="danger"
        title="Supprimer cet indice ?"
        description={`« ${indice.label ?? indice.code ?? ''} » sera définitivement retiré du référentiel, avec l'intégralité de son historique (${periods.length} valeur${periods.length > 1 ? 's' : ''}). Cette action est irréversible.`}
        confirmLabel="Supprimer définitivement"
      />
    </div>
  );
};

export default IndiceDetails;
